import { useEffect, useRef } from "react";
import { AREAS, MAX_SCORE } from "@/lib/heat";

const LOGO_W = 1890;
const LOGO_H = 1916;
const CX = 931 / LOGO_W;
const CY = 1064 / LOGO_H;
const R = 1060 / LOGO_W;

const load = (src) =>
  new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });

export const ColoringFlower = ({ size = 560, testId = "coloring-flower" }) => {
  const ref = useRef(null);

  useEffect(() => {
    let alive = true;
    Promise.all([load("/flower-logo.png"), load("/flower-outline.png")]).then(([logo, outline]) => {
      if (!alive || !ref.current) return;
      const dpr = 2;
      const W = size * dpr;
      const c = ref.current;
      c.width = W;
      c.height = W;
      const ctx = c.getContext("2d");
      const pad = W * 0.17;
      const lw = W - pad * 2;
      const lh = lw * (LOGO_H / LOGO_W);
      const lx = pad;
      const ly = pad + (lw - lh) / 2;
      const cx = lx + CX * lw;
      const cy = ly + CY * lh;
      const r = R * lw;

      const layer = document.createElement("canvas");
      layer.width = W;
      layer.height = W;
      const g = layer.getContext("2d");
      g.strokeStyle = "#94a3b8";
      g.lineWidth = 1.5 * dpr;
      [11, 20, 28, MAX_SCORE].forEach((v) => {
        g.setLineDash([6 * dpr, 6 * dpr]);
        g.beginPath();
        g.arc(cx, cy, (v / MAX_SCORE) * r, 0, Math.PI * 2);
        g.stroke();
      });
      g.setLineDash([]);
      AREAS.forEach((a) => {
        const t = (a.angle * Math.PI) / 180;
        g.beginPath();
        g.moveTo(cx, cy);
        g.lineTo(cx + Math.cos(t) * r * 1.05, cy + Math.sin(t) * r * 1.05);
        g.stroke();
        g.fillStyle = "#475569";
        g.font = `${10 * dpr}px Plus Jakarta Sans, sans-serif`;
        g.textAlign = "center";
        g.textBaseline = "middle";
        [11, 20, 28, 36].forEach((v) => {
          const d = (v / MAX_SCORE) * r;
          g.fillText(String(v), cx + Math.cos(t) * d + Math.sin(t) * 10 * dpr, cy + Math.sin(t) * d - Math.cos(t) * 10 * dpr);
        });
      });
      g.globalCompositeOperation = "destination-in";
      g.drawImage(logo, lx, ly, lw, lh);

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, W, W);
      ctx.drawImage(layer, 0, 0);
      ctx.drawImage(outline, lx, ly, lw, lh);

      ctx.fillStyle = "#1F4266";
      ctx.font = `600 ${15 * dpr}px Outfit, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      AREAS.forEach((a) => {
        const t = (a.angle * Math.PI) / 180;
        const d = r * 1.12;
        ctx.fillText(a.label, cx + Math.cos(t) * d, cy + Math.sin(t) * d);
        ctx.font = `${11 * dpr}px Plus Jakarta Sans, sans-serif`;
        ctx.fillStyle = "#64748b";
        ctx.fillText("Score: ____ / 36", cx + Math.cos(t) * d, cy + Math.sin(t) * d + 17 * dpr);
        ctx.fillStyle = "#1F4266";
        ctx.font = `600 ${15 * dpr}px Outfit, sans-serif`;
      });
    });
    return () => {
      alive = false;
    };
  }, [size]);

  return <canvas ref={ref} data-testid={testId} style={{ width: "100%", maxWidth: size, aspectRatio: "1 / 1" }} aria-label="Flower to color in" />;
};
